import { useState } from "react";
import { motion } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA } from "@/components/CTA";
import { Tick } from "@/components/Annotation";
import { SectionLabel, StickyNote, Tape } from "@/components/Notebook";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

// Kept English across all locales per brand rules
const LOGOS = [
  { src: "/logos/amazon.png",  alt: "Amazon",                        w: "w-[150px] md:w-[190px]" },
  { src: "/logos/ert.png",     alt: "ΕΡΤ · Hellenic Broadcasting",   w: "w-[70px]  md:w-[90px]"  },
  { src: "/logos/avocado.png", alt: "Avocado Learning Experiences",  w: "w-[170px] md:w-[210px]" },
];

const FEEDBACK_PHRASES = [
  "Interaction and material discussed",
  "Great facilitator — very good work",
  "The facilitator was very engaging",
  "Interactive activities. Passion. Respect. Politeness.",
  "Great presentation and very helpful",
  "Engaging, to the point and resourceful under different circumstances",
  "Tips for grounding · the energy of the presenter · the actual examples",
  "Interactive — with real examples and scenarios",
  "The role play with the actor and the body exercises",
  "Interactive for most of its duration — a quality often missing from workshops",
  "The interactive exercises and the incorporation of physical practice",
];

const HwwTrust = ({ t }) => (
  <section className="relative z-10 py-14 md:py-20 border-y border-ink/10 bg-paper-deep/40" data-testid="hww-trust">
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-8 md:mb-10">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-ink-soft text-center">
        {t("hww.trust_label")}
      </p>
    </Reveal>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-12 md:gap-20 mb-10 md:mb-14">
      {LOGOS.map((l) => (
        <img key={l.alt} src={l.src} alt={l.alt} draggable={false}
          className={`${l.w} h-auto opacity-90 hover:opacity-100 transition-opacity duration-500 select-none`} />
      ))}
    </div>
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-5 md:mb-6">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta text-center">
        {t("hww.trust_marquee_label")}
      </p>
    </Reveal>
    <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
      <div className="flex w-max animate-marquee items-center gap-10 md:gap-14 whitespace-nowrap">
        {[...FEEDBACK_PHRASES, ...FEEDBACK_PHRASES].map((phrase, i) => (
          <span key={i} className="inline-flex items-center gap-10 md:gap-14">
            <span className="font-serif italic text-lg md:text-2xl text-ink/55 hover:text-ink transition-colors duration-500">
              &ldquo;{phrase}&rdquo;
            </span>
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta/60" />
          </span>
        ))}
      </div>
    </div>
  </section>
);

const HSP_LAYERS = [
  "Mindset & Intention", "Breathing", "Body Awareness", "Posture", "Grounding",
  "Movement", "Voice", "Facial Expression", "Eye Contact", "Gesture",
  "Space Awareness", "Message Structure", "Storytelling", "Audience Connection",
  "Emotional Authenticity", "Executive Presence", "Handling Pressure",
];

const REHEARSAL_LAYERS = [
  { n: 1, name: "Structure", detail: "Only structure. The bones of the message." },
  { n: 2, name: "+ Intention", detail: "Why you are saying it — before how you say it." },
  { n: 3, name: "+ Breathing", detail: "Regulate the nervous system. Breath first." },
  { n: 4, name: "+ Posture", detail: "The body settles. Presence begins." },
  { n: 5, name: "+ Voice", detail: "The instrument comes online — grounded, not performative." },
  { n: 6, name: "+ Eye contact", detail: "Connect the message to human beings in the room." },
  { n: 7, name: "+ Gestures", detail: "The body starts illustrating the idea." },
  { n: 8, name: "+ Movement in space", detail: "The stage becomes a map of your thinking." },
  { n: 9, name: "+ Emotional connection", detail: "The whole system lands as one authentic voice." },
];

const RESET_STEPS = [
  { key: "breath", label: "Breath", note: "Regulates the nervous system." },
  { key: "body", label: "Body", note: "Creates stability and grounding." },
  { key: "voice", label: "Voice", note: "Reflects your internal state." },
  { key: "intention", label: "Intention", note: "Shifts attention from self to audience." },
];

const CYCLE = ["Practice", "Feedback", "Reflection", "Adjustment", "New Practice"];

const PRESSURE_CONTEXTS = [
  "Executive meetings", "Board presentations", "Conference talks",
  "Media interviews", "Investor pitches", "Unexpected questions", "Difficult stakeholders",
];

const FRAMEWORKS = [
  {
    id: "holistic",
    n: "01",
    title: "Holistic Stage Presence™",
    lead: "Communication is an integrated system — not isolated techniques.",
    render: () => (
      <div>
        <p className="font-body text-ink-soft leading-relaxed">
          Most people try to fix isolated problems — <em>more confidence</em>, <em>better body language</em>,
          <em> a stronger voice</em>. We don&rsquo;t coach isolated skills. We develop the whole system,
          because every element influences the others.
        </p>
        <SectionLabel className="mt-6 mb-4 block">the 17 integrated layers</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {HSP_LAYERS.map((l) => (
            <span key={l} className="inline-flex items-center gap-2 px-3 py-1.5 border border-ink/25 text-ink font-body text-sm">
              {l}
            </span>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "layers",
    n: "02",
    title: "Layer-by-Layer Rehearsal™",
    lead: "One layer at a time. Then integrate.",
    render: () => <LayerByLayer />,
  },
  {
    id: "reset",
    n: "03",
    title: "The Presence Reset",
    lead: "Presence begins before speaking.",
    render: () => <PresenceReset />,
  },
  {
    id: "rehearsal",
    n: "04",
    title: "Communication as Rehearsal",
    lead: "Our sessions resemble a rehearsal room — not a classroom.",
    render: () => (
      <div>
        <p className="font-body text-ink-soft leading-relaxed">
          People don&rsquo;t sit and receive theory. They experiment. Observe. Adjust. Repeat.
          Reflect. Try again. Communication improves through <span className="ink-underline">repetition with awareness</span> —
          not repetition alone.
        </p>
        <SectionLabel className="mt-6 mb-4 block">every session — the cycle</SectionLabel>
        <div className="flex flex-wrap items-center gap-3">
          {CYCLE.map((c, i) => (
            <span key={c} className="inline-flex items-center gap-3">
              <span className="font-serif italic text-lg text-ink">{c}</span>
              {i < CYCLE.length - 1 && <span className="font-hand text-terracotta text-xl">→</span>}
            </span>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "pressure",
    n: "05",
    title: "Communication Under Pressure",
    lead: "The objective is to remain yourself under pressure.",
    render: () => (
      <div>
        <p className="font-body text-ink-soft leading-relaxed">
          People rarely fail because they don&rsquo;t know what to say. They fail because pressure
          changes how they communicate. We rehearse pressure directly.
        </p>
        <SectionLabel className="mt-6 mb-4 block">simulated contexts</SectionLabel>
        <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
          {PRESSURE_CONTEXTS.map((p) => (
            <li key={p} className="flex items-start gap-3">
              <Tick className="w-4 h-4 mt-1 shrink-0" />
              <span className="font-body text-ink">{p}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: "personal",
    n: "06",
    title: "Personalised Development",
    lead: "The methodology stays the same. The path through it changes.",
    render: () => (
      <p className="font-body text-ink-soft leading-relaxed">
        Every client has different strengths, communication habits and goals. We adapt the
        rehearsal process — never force people into a predefined style. We don&rsquo;t create
        performers. We help professionals discover their own <span className="ink-underline">authentic voice</span>.
      </p>
    ),
  },
];

/* ── Interactive: Layer-by-Layer builder ────────────────────────────── */
function LayerByLayer() {
  const [active, setActive] = useState(1);
  return (
    <div>
      <p className="font-body text-ink-soft leading-relaxed">
        Click each layer to build the presentation. Each new layer only integrates once the
        previous one becomes natural.
      </p>
      <div className="mt-6 grid md:grid-cols-12 gap-4">
        <div className="md:col-span-5 space-y-2">
          {REHEARSAL_LAYERS.map((l) => (
            <button
              key={l.n}
              onClick={() => setActive(l.n)}
              data-testid={`layer-${l.n}`}
              className={`w-full text-left px-4 py-3 border transition-colors ${
                l.n === active
                  ? "border-terracotta bg-terracotta/5 text-ink"
                  : l.n < active
                  ? "border-ink/20 bg-paper-deep/30 text-ink"
                  : "border-ink/15 text-ink-soft hover:border-ink/40"
              }`}
            >
              <span className="font-serif text-lg">
                Layer {l.n} — <span className="italic">{l.name}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="md:col-span-7">
          <div className="relative bg-paper border border-ink/12 shadow-[3px_6px_22px_rgba(27,27,24,0.08)] p-6 md:p-8 min-h-[280px]">
            <Tape className="absolute -top-4 left-6" rotate={-4} width={100} />
            <SectionLabel className="mb-4 block">layer {active}</SectionLabel>
            <p className="font-serif text-2xl text-ink leading-snug mb-3">
              {REHEARSAL_LAYERS[active - 1].name}
            </p>
            <p className="font-body text-ink-soft leading-relaxed">
              {REHEARSAL_LAYERS[active - 1].detail}
            </p>
            <div className="mt-6 h-2 w-full bg-ink/10 relative overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 bg-mustard"
                initial={false}
                animate={{ width: `${(active / REHEARSAL_LAYERS.length) * 100}%` }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
            <p className="mt-2 font-hand text-terracotta">
              {active}/{REHEARSAL_LAYERS.length} layers integrated
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Interactive: Presence Reset ────────────────────────────────────── */
function PresenceReset() {
  const [active, setActive] = useState(0);
  return (
    <div>
      <p className="font-body text-ink-soft leading-relaxed">
        A short sequence used to reconnect with yourself before reconnecting with the audience.
        Tap each step to see why it matters.
      </p>
      <div className="mt-6 grid grid-cols-4 gap-3">
        {RESET_STEPS.map((s, i) => (
          <button
            key={s.key}
            onClick={() => setActive(i)}
            data-testid={`reset-${s.key}`}
            className={`text-center py-4 border transition-colors ${
              i === active
                ? "border-terracotta bg-terracotta/5"
                : "border-ink/20 hover:border-ink/40"
            }`}
          >
            <span className="font-serif text-xl md:text-2xl text-ink block">{s.label}</span>
            <span className="font-hand text-terracotta text-lg mt-1 block">{i < RESET_STEPS.length - 1 ? "↓" : "•"}</span>
          </button>
        ))}
      </div>
      <div className="mt-5 p-5 bg-paper-deep/40 border border-ink/12">
        <p className="font-serif text-xl text-ink italic">{RESET_STEPS[active].label}</p>
        <p className="mt-2 font-body text-ink-soft">{RESET_STEPS[active].note}</p>
      </div>
    </div>
  );
}

/* ── Working together — 3-track timeline ────────────────────────────── */
const TRACKS = {
  coaching: {
    label: "1:1 Coaching",
    steps: [
      { t: "Free Consultation", d: "A strategy conversation about your goals, challenges, timeline and communication context. The objective is not to sell — it's to determine whether we're the right fit for each other." },
      { t: "Proposal", d: "A tailored proposal covering objectives, recommended format, timeline, investment and expected outcomes." },
      { t: "Adaptation & Approval", d: "You review the proposal. We adjust the programme if needed until it fits your needs. Once approved, we move forward." },
      { t: "Payment & Invoice", d: "Payment completed. Invoice issued. Logistics confirmed." },
      { t: "Scheduling", d: "Sessions booked. Pre-work and preparation material shared where relevant. The rehearsal journey officially begins." },
    ],
  },
  corporate: {
    label: "Corporate",
    steps: [
      { t: "Initial Conversation", d: "Meet with L&D or leadership sponsors to understand context, participants, objectives and constraints." },
      { t: "Programme Design", d: "Co-designed proposal — content, format, duration, delivery, sponsors, KPIs — shaped around the outcome you need." },
      { t: "Adaptation & Approval", d: "Iterate the programme with your team until it aligns with the wider L&D architecture and business context." },
      { t: "Contract & Logistics", d: "Contract signed. Rooms, technology, materials, participant briefings and pre-work all confirmed." },
      { t: "Delivery & Reinforcement", d: "Programme delivered. Follow-ups, tools and reinforcement built in so the change survives beyond the workshop room." },
    ],
  },
  collaboration: {
    label: "L&D Collaboration",
    steps: [
      { t: "Intro Conversation", d: "A quiet conversation with the L&D or consulting partner to understand your client, their objectives and where we fit." },
      { t: "Scoping the Role", d: "Define our role — specialist facilitator, co-designer, co-facilitator, white-label lead — and the boundaries around it." },
      { t: "Co-Design", d: "Work with your design team to shape sessions, materials and rehearsal formats that carry your brand and methodology." },
      { t: "Agreement & Logistics", d: "Partnership agreement, rates, IP and delivery logistics confirmed." },
      { t: "Delivery & Feedback", d: "Deliver the programme with your team. Feedback loop keeps future collaborations sharper." },
    ],
  },
};

function WorkingTogether() {
  const { t } = useLang();
  const [track, setTrack] = useState("coaching");
  const labels = {
    coaching: t("hww.track_label_coaching"),
    corporate: t("hww.track_label_corporate"),
    collaboration: t("hww.track_label_collaboration"),
  };
  const steps = t(`hww.steps.${track}`) || TRACKS[track].steps;
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-8">
        {Object.keys(TRACKS).map((k) => (
          <button
            key={k}
            onClick={() => setTrack(k)}
            data-testid={`track-${k}`}
            className={`px-5 py-3 font-body text-xs uppercase tracking-[0.2em] border transition-colors ${
              k === track
                ? "bg-ink text-paper border-ink"
                : "text-ink border-ink/25 hover:border-ink"
            }`}
          >
            {labels[k]}
          </button>
        ))}
      </div>
      <ol className="md:grid md:grid-cols-5 md:gap-4 space-y-6 md:space-y-0">
        {steps.map((s, i) => (
          <li key={s.t} className="relative flex md:flex-col gap-4 md:gap-0">
            <div className="md:flex md:items-center md:mb-4">
              <span className="font-serif text-4xl md:text-5xl text-terracotta leading-none block">
                0{i + 1}
              </span>
              {i < steps.length - 1 && (
                <span className="hidden md:inline-block flex-1 h-px bg-ink/25 ml-3" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-serif text-lg md:text-xl text-ink leading-tight">{s.t}</h4>
              <p className="mt-2 font-body text-sm text-ink-soft leading-relaxed">{s.d}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function HowWeWork() {
  const { t } = useLang();
  const [openId, setOpenId] = useState("holistic");
  return (
    <>
      <PageMeta
        titleKey="meta.hww_title"
        descriptionKey="meta.hww_description"
        path="/how-we-work"
      />
      <PageHero
        testId="hww-hero"
        eyebrow={t("hww.hero_eyebrow")}
        headline={[t("hww.hero_h1_a"), t("hww.hero_h1_b")]}
        emphasis={t("hww.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("hww.hero_intro")}
      />

      <HwwTrust t={t} />

      {/* Frameworks accordion */}
      <section className="relative z-10 pb-24 md:pb-32" data-testid="hww-frameworks">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 space-y-4">
          {FRAMEWORKS.map((f) => {
            const open = openId === f.id;
            return (
              <StaggerItem key={f.id}>
                <div
                  className={`border transition-colors ${open ? "border-terracotta" : "border-ink/15 hover:border-ink/40"} bg-paper`}
                  data-testid={`framework-${f.id}`}
                >
                  <button
                    onClick={() => setOpenId(open ? "" : f.id)}
                    className="w-full text-left flex items-start justify-between gap-6 p-6 md:p-8"
                    aria-expanded={open}
                  >
                    <div className="flex items-start gap-5 flex-1">
                      <span className="font-serif text-3xl md:text-4xl text-terracotta/70 leading-none shrink-0">
                        {f.n}
                      </span>
                      <div>
                        <h3 className="font-serif text-xl md:text-2xl text-ink leading-tight">{f.title}</h3>
                        <p className="mt-2 font-hand text-terracotta text-lg leading-snug">{f.lead}</p>
                      </div>
                    </div>
                    <span className={`font-hand text-terracotta text-3xl leading-none shrink-0 transition-transform ${open ? "rotate-45" : ""}`}>
                      +
                    </span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 md:px-8 pb-8 md:pl-[92px]">{f.render()}</div>
                  </motion.div>
                </div>
              </StaggerItem>
            );
          })}
        </div>
      </section>

      {/* Working together */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="hww-working"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-10 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("hww.working_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("hww.working_h1")}
              <br />
              <span className="italic">{t("hww.working_h2")}</span>
            </h2>
            <p className="mt-5 font-body text-lg text-ink-soft leading-relaxed">
              {t("hww.working_sub")}
            </p>
          </Reveal>
          <WorkingTogether />
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="hww-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-paper">
              {t("hww.final_h1")}
              <br />
              <span className="italic">{t("hww.final_h2")}</span>
            </h2>
            <StickyNote className="mt-8 inline-block" rotate={-3} color="#F2EBDA">
              {t("hww.final_sticky")}
            </StickyNote>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.booking} testId="hww-book-cta" location="final_cta" targetService="general_booking">
              {t("hww.final_cta_book")}
            </PrimaryCTA>
            <OutlineCTA
              href={ROUTES.contact}
              className="!border-paper/75 !text-paper hover:!border-mustard hover:!text-mustard"
              testId="hww-contact-cta"
              location="final_cta"
              targetAssetId="contact_request"
            >
              {t("hww.final_cta_contact")}
            </OutlineCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
